import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, AlertTriangle, Plus, X, Radar, Mail, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import SmartLink from "@/components/SmartLink";

const money = (v) =>
  v == null ? "—" : `$${v >= 1000000 ? (v / 1000000).toFixed(1) + "M" : v >= 1000 ? Math.round(v / 1000) + "K" : v}`;

function Section({ label, children, testId }) {
  return (
    <section data-testid={testId} className="soft-card mx-4 my-4 px-5 py-6 rise-in">
      <p className="overline-label mb-4">{label}</p>
      {children}
    </section>
  );
}

function SuggestedTaskCard({ t, onDone }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(t.task);
  const [due, setDue] = useState(t.due_date);
  const approve = async () => {
    try {
      await api.post(`/suggested-tasks/${t.id}/approve`, { task: text, due_date: due });
      toast.success("Added to follow-up queue");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  const dismiss = async () => {
    try {
      await api.post(`/suggested-tasks/${t.id}/dismiss`);
      toast("Dismissed — sender noise will be learned");
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid={`suggested-task-${t.id}`} className="bg-secondary/70 rounded-xl p-4 space-y-2">
      {editing ? (
        <div className="space-y-2">
          <input data-testid="suggested-task-edit-text" className="tactical-input" value={text} onChange={(e) => setText(e.target.value)} />
          <input data-testid="suggested-task-edit-date" type="date" className="tactical-input" value={due} onChange={(e) => setDue(e.target.value)} />
        </div>
      ) : (
        <>
          <p className="text-sm font-semibold">{text}</p>
          <p className="text-xs text-muted-foreground">
            due <span className="font-mono">{due}</span> · from {t.sender}
            {t.source_link && (
              <SmartLink testId={`task-email-link-${t.id}`} href={t.source_link} className="text-primary ml-2 underline">open email</SmartLink>
            )}
          </p>
        </>
      )}
      <div className="flex gap-2">
        <button data-testid={`approve-task-${t.id}`} onClick={approve} className="flex-1 h-10 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest tap-btn">Approve</button>
        <button data-testid={`edit-task-${t.id}`} onClick={() => setEditing(!editing)} className="h-10 px-3 border border-border text-muted-foreground tap-btn"><Pencil size={14} /></button>
        <button data-testid={`dismiss-task-${t.id}`} onClick={dismiss} className="h-10 px-3 border border-border text-destructive tap-btn"><X size={14} /></button>
      </div>
    </div>
  );
}

export default function Today() {
  const [data, setData] = useState(null);
  const [newPriority, setNewPriority] = useState("");

  const load = useCallback(() => {
    api.get("/dashboard/today").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="p-6 text-muted-foreground font-mono text-sm">LOADING...</p>;

  const currentId = data.priorities.find((p) => !p.done)?.id;

  const addPriority = async () => {
    if (!newPriority.trim()) return;
    try {
      await api.post("/priorities", { text: newPriority.trim(), position: data.priorities.length + 1 });
      setNewPriority("");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const togglePriority = async (p) => {
    await api.put(`/priorities/${p.id}`, { done: !p.done });
    load();
  };

  const doneFollowup = async (f) => {
    await api.post(`/followups/${f.id}/done`);
    toast.success("Done");
    load();
  };
  const pushFollowup = async (f) => {
    await api.post(`/followups/${f.id}/push`);
    toast("Pushed to tomorrow");
    load();
  };

  return (
    <div data-testid="today-dashboard" className="pb-4">
      <div className="px-5 pt-8 pb-1 flex items-end justify-between">
        <div>
          <p className="overline-label text-primary">Command Center</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-primary">Today</h1>
        </div>
        <p className="font-mono text-xs text-muted-foreground">{data.date}</p>
      </div>

      {data.new_bids_count > 0 && (
        <Link to="/bids" data-testid="new-bids-banner" className="mx-4 mt-4 flex items-center justify-between bg-primary text-primary-foreground rounded-2xl px-5 py-3.5 shadow-sm tap-btn">
          <span className="flex items-center gap-2 text-sm font-semibold"><Radar size={16} />{data.new_bids_count} new bid match{data.new_bids_count > 1 ? "es" : ""} to review</span>
          <ArrowRight size={16} />
        </Link>
      )}

      <Section label="Top 3 Priorities" testId="priorities-section">
        <div className="space-y-3">
          {data.priorities.map((p) => (
            <button
              key={p.id}
              data-testid={`priority-item-${p.position}`}
              onClick={() => togglePriority(p)}
              className="w-full text-left flex items-start gap-3 tap-btn"
            >
              <span className={`font-mono text-sm mt-2 ${p.done ? "text-muted-foreground" : p.id === currentId ? "text-[#C9A227]" : "text-primary"}`}>0{p.position}</span>
              <span className={`font-display text-2xl sm:text-3xl tracking-tight leading-tight ${p.done ? "line-through text-muted-foreground" : p.id === currentId ? "text-[#C9A227]" : ""}`}>
                {p.text}
              </span>
              {p.carried_over && !p.done && <span className="overline-label mt-2 border border-border px-1">carried</span>}
            </button>
          ))}
          {data.priorities.length < 3 && (
            <div className="flex gap-2">
              <input
                data-testid="new-priority-input"
                className="tactical-input"
                placeholder={`Set priority ${data.priorities.length + 1}...`}
                value={newPriority}
                onChange={(e) => setNewPriority(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addPriority()}
              />
              <button data-testid="add-priority-button" onClick={addPriority} className="h-11 px-4 bg-secondary border border-border text-primary tap-btn"><Plus size={16} /></button>
            </div>
          )}
        </div>
      </Section>

      <Section label="Follow-ups Due" testId="followups-due-section">
        {data.followups_due.length === 0 && <p className="text-sm text-muted-foreground">Clear. Nothing due today.</p>}
        <div className="space-y-1">
          {data.followups_due.map((f) => (
            <div key={f.id} data-testid={`followup-due-${f.id}`} className="flex items-center gap-2 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{f.text}</p>
                {f.overdue && <p className="text-[10px] uppercase tracking-widest text-destructive">overdue · rolled forward</p>}
              </div>
              <button data-testid={`followup-done-${f.id}`} onClick={() => doneFollowup(f)} className="h-11 px-3 border border-[#7A9B76] text-[#7A9B76] text-xs font-bold uppercase tap-btn flex items-center gap-1"><Check size={14} />Done</button>
              <button data-testid={`followup-push-${f.id}`} onClick={() => pushFollowup(f)} className="h-11 px-3 border border-border text-muted-foreground text-xs font-bold uppercase tap-btn">Push</button>
            </div>
          ))}
        </div>
      </Section>

      <Section label="Proposals In Flight" testId="proposals-section">
        {data.proposals_in_flight.length === 0 && <p className="text-sm text-muted-foreground">No proposals in progress or submitted.</p>}
        <div className="space-y-1">
          {data.proposals_in_flight.map((o) => (
            <div key={o.id} className="flex items-center justify-between py-2.5" data-testid={`proposal-${o.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold truncate">{o.client_name}</p>
                <p className="text-xs text-muted-foreground">{o.stage} · {money(o.value)}</p>
              </div>
              <p className={`font-mono text-xl font-bold ${o.days_left == null ? "text-primary" : o.days_left < 3 ? "text-destructive" : o.days_left <= 7 ? "text-[#B58530]" : "text-primary"}`}>
                {o.days_left == null ? "—" : `${String(Math.max(o.days_left, 0)).padStart(2, "0")}D`}
              </p>
            </div>
          ))}
        </div>
      </Section>

      <Section label="Pipeline Value" testId="pipeline-summary-section">
        {(() => {
          const open = data.pipeline.filter((s) => !["Won", "Lost"].includes(s.stage));
          const gross = open.reduce((a, s) => a + s.value, 0);
          const weighted = open.reduce((a, s) => a + (s.weighted ?? s.value), 0);
          return (
            <div className="mb-3">
              <p className="font-mono text-3xl font-bold text-primary" data-testid="pipeline-weighted-value">{money(weighted)}</p>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                weighted expected · <span className="font-mono" data-testid="pipeline-total-value">{money(gross)}</span> gross
              </p>
            </div>
          );
        })()}
        <div className="space-y-1.5">
          {data.pipeline.map((s) => {
            const max = Math.max(...data.pipeline.map((x) => x.value), 1);
            return (
              <div key={s.stage} className="flex items-center gap-2 text-xs">
                <span className="w-28 shrink-0 text-muted-foreground uppercase tracking-wider text-[10px]">{s.stage}</span>
                <div className="flex-1 h-3 bg-secondary rounded-full overflow-hidden">
                  <div className={`h-3 ${s.stage === "Won" ? "bg-[#7A9B76]" : s.stage === "Lost" ? "bg-muted" : "bg-primary"}`} style={{ width: `${(s.value / max) * 100}%` }} />
                </div>
                <span className="font-mono w-14 text-right">{money(s.value)}</span>
              </div>
            );
          })}
        </div>
      </Section>

      {data.flagged.length > 0 && (
        <Section label="Needs Next Action" testId="flagged-section">
          {data.flagged.map((o) => (
            <Link to="/pipeline" key={o.id} data-testid={`flagged-opp-${o.id}`} className="flex items-center gap-2 bg-destructive/10 rounded-xl px-4 py-3 mb-2 tap-btn">
              <AlertTriangle size={16} className="text-destructive shrink-0" />
              <span className="text-sm font-semibold text-destructive truncate">{o.client_name} has no next action</span>
            </Link>
          ))}
        </Section>
      )}

      {data.suggested_tasks.length > 0 && (
        <Section label="Suggested To-Dos · From Email" testId="suggested-tasks-section">
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground flex items-center gap-1"><Mail size={12} /> AI-extracted from your inbox — nothing added without your approval.</p>
            {data.suggested_tasks.map((t) => (
              <SuggestedTaskCard key={t.id} t={t} onDone={load} />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
